import { AnimatePresence, motion } from 'framer-motion';
import { BORDER_RADIUS, SHADOWS } from '../../../styles/UISystem';

/**
 * ContactTypeDropdown
 *
 * Frosted glass popover for selecting a contact type. Anchored to a trigger
 * element (positioned absolute, bottom-right origin). Used in the Anon
 * exchange overlays.
 *
 * Extracted from ChatInputSharing's renderDropdown — same visual design:
 * blur(40px), spring animation, icon + label rows.
 *
 * @param {boolean} open - Whether the dropdown is visible
 * @param {Array} types - Contact type objects from getAvailableContactTypes (must have .id, .label, .Icon)
 * @param {function} onSelect - Called with type id string
 * @param {function} onClose - Called when backdrop is tapped
 */
const ContactTypeDropdown = ({ open, types, onSelect, onClose }) => {
  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              zIndex: 50,
            }}
            onClick={onClose}
          />
          {/* Dropdown card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.6, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.6, y: 10 }}
            transition={{
              type: 'spring',
              stiffness: 400,
              damping: 22,
              mass: 0.8,
            }}
            style={{
              position: 'absolute',
              bottom: '100%',
              right: -4,
              width: '220px',
              marginBottom: '4px',
              backgroundColor: 'rgba(251, 251, 253, 0.95)',
              WebkitBackdropFilter: 'blur(40px) saturate(150%)',
              backdropFilter: 'blur(40px) saturate(150%)',
              borderRadius: BORDER_RADIUS.PILL,
              border: '0.5px solid rgba(0, 0, 0, 0.08)',
              boxShadow: SHADOWS.TIER3_UP,
              zIndex: 51,
              overflow: 'hidden',
              padding: '6px 0',
              transformOrigin: 'bottom right',
            }}
          >
            {types.map((type) => {
              const IconComponent = type.Icon;
              return (
                <button
                  key={type.id}
                  onClick={() => onSelect(type.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    padding: '12px 18px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.05)',
                    gap: '14px',
                  }}
                >
                  <IconComponent
                    size={26}
                    color="#1c1c1e"
                    style={{
                      width: 26,
                      height: 26,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontSize: '22px',
                      fontWeight: 500,
                      color: '#1c1c1e',
                      lineHeight: '28px',
                    }}
                  >
                    {type.label}
                  </span>
                </button>
              );
            })}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ContactTypeDropdown;
